import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, FileText, ArrowLeft, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@tremor/react';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { formatTextWithLinks } from '../../../utils/formatters';
import AddEOIResponseModal from './components/AddEOIResponseModal';
import EditEOIResponseModal from './components/EditEOIResponseModal';
import type { EOIResponse } from '../../../types/eoi-response';
import type { Applicant } from '../../../types/applicant';
import type { LeaseEOI } from '../../../types/lease-eoi';

export default function EOIResponsesAdmin() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<EOIResponse | undefined>();
  const queryClient = useQueryClient();

  const { data: responses, isLoading } = useQuery({
    queryKey: ['admin', 'eoi-responses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eoi_responses')
        .select(`
          *,
          eoi_response_attachments (*)
        `)
        .order('reoi_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Fetch related EOIs and applicants
      const eoiIds = [...new Set(data.map(r => r.reoi_eoi).filter(id => id && !isNaN(parseInt(id))))];
      const appIds = [...new Set(data.map(r => r.reoi_app).filter(id => id && !isNaN(parseInt(id))))];
      
      const [eoisResult, applicantsResult] = await Promise.all([
        eoiIds.length > 0 ? supabase.from('lease_eois').select('eoi_id, eoi_lot').in('eoi_id', eoiIds) : { data: [] },
        appIds.length > 0 ? supabase.from('applicants').select('appl_id, appl_name').in('appl_id', appIds) : { data: [] }
      ]);
      
      // Create lookup maps
      const eoiMap = new Map(eoisResult.data?.map(e => [e.eoi_id.toString(), e]));
      const appMap = new Map(applicantsResult.data?.map(a => [a.appl_id.toString(), a]));
      
      // Enhance response data with related info
      return data.map(response => ({
        ...response,
        eoiDetails: eoiMap.get(response.reoi_eoi),
        applicantDetails: appMap.get(response.reoi_app)
      }));
    }
  });

  // Helper function to get status badge
  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'Y':
        return <Badge color="green">Successful</Badge>;
      case 'N':
        return <Badge color="red">Unsuccessful</Badge>;
      case 'P':
        return <Badge color="yellow">Under Review</Badge>;
      default:
        return <Badge color="gray">{status || 'Unknown'}</Badge>;
    }
  };


  const filteredResponses = responses?.filter(response => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        response.reoi_eoi?.toLowerCase().includes(query) ||
        response.reoi_app?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">EOI Responses</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage responses to Expressions of Interest
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedResponse(undefined);
              setIsFormModalOpen(true);
            }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Response
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search responses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Filter className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EOI Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EoI Applicant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submission Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes and Docs
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredResponses?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No EOI responses found
                  </td>
                </tr>
              ) : (
                filteredResponses?.map((response) => (
                  <tr key={response.reoi_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedResponse(response);
                          setIsFormModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {response.reoi_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {response.eoiDetails ? `${response.reoi_eoi} - ${response.eoiDetails.eoi_lot}` : response.reoi_eoi || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {response.applicantDetails ? response.applicantDetails.appl_name : response.reoi_app || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(response.reoi_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getStatusBadge(response.reoi_success)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTextWithLinks(response.reoi_application) || 'No notes'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      {/* Add EOI Response Modal */}
      {isFormModalOpen && !selectedResponse && (
        <AddEOIResponseModal
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'eoi-responses'] });
          }}
        />
      )}

      {/* Edit EOI Response Modal */}
      {isFormModalOpen && selectedResponse && (
        <EditEOIResponseModal
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedResponse(undefined);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'eoi-responses'] });
          }}
          eoiResponse={selectedResponse}
        />
      )}

    </div>
  );
}
